/**
 * Account-timezone picker options.
 *
 * Built from the FULL IANA zone set (Intl.supportedValuesOf) so EVERY user's system zone is
 * selectable (the picker dialog has a search box). Common zones get a friendly label; the rest
 * show the IANA name. There is NO blank / "use account default" option — every user has a real
 * zone (auto-defaulted to their system zone on first load). Using the runtime's own IANA set also
 * avoids the Asia/Kolkata-vs-Asia/Calcutta canonical mismatch: whatever name this runtime
 * canonicalizes to is exactly what is offered + what systemDisplayTimezone() returns, so the
 * stored value always matches an option.
 */
const FRIENDLY_LABELS: Record<string, string> = {
  "America/Los_Angeles": "Pacific (US)",
  "America/Denver": "Mountain (US)",
  "America/Chicago": "Central (US)",
  "America/New_York": "Eastern (US)",
  "America/Anchorage": "Alaska (US)",
  "Pacific/Honolulu": "Hawaii (US)",
  "Europe/London": "London (UK)",
  "Europe/Paris": "Central Europe",
  "Asia/Kolkata": "India (IST)",
  "Asia/Calcutta": "India (IST)",
  "Asia/Singapore": "Singapore",
  "Australia/Sydney": "Sydney",
};

function allIanaZones(): string[] {
  try {
    const intlAny = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
    if (typeof intlAny.supportedValuesOf === "function") {
      const zones = intlAny.supportedValuesOf("timeZone");
      if (Array.isArray(zones) && zones.length > 0) return zones;
    }
  } catch {
    /* fall through to the curated fallback below */
  }
  // Fallback for runtimes without Intl.supportedValuesOf — curated set, still NO blank option.
  return Object.keys(FRIENDLY_LABELS);
}

export const DISPLAY_TIMEZONES: { value: string; text: string }[] = allIanaZones().map((z) => ({
  value: z,
  text: FRIENDLY_LABELS[z] || z.replace(/_/g, " "),
}));

/**
 * The viewer's SYSTEM (browser) IANA zone, as a real selectable option. Because the option list
 * IS the full IANA set from THIS runtime, the resolved zone is always present -> always
 * selectable (no Kolkata/Calcutta mismatch). Returns "" only if Intl fails (the picker then stays
 * empty and the chain floors to Pacific). Used to PREFILL the picker + as the value the Option-B
 * auto-write stores. NOT part of the resolution chain (display stays user_preferences -> Pacific).
 */
export function systemDisplayTimezone(): string {
  try {
    const z = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    return DISPLAY_TIMEZONES.some((o) => o.value === z) ? z : "";
  } catch {
    return "";
  }
}

/**
 * The runtime's CANONICAL IANA name for a zone. Maps aliases to one name (Asia/Kolkata AND
 * Asia/Calcutta both -> Asia/Calcutta on this ICU; US/Pacific -> America/Los_Angeles), so two
 * spellings of the same zone compare equal.
 */
function canonicalZone(zone: string): string {
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone: zone }).resolvedOptions().timeZone;
  } catch {
    return zone;
  }
}

/**
 * The DISPLAY_TIMEZONES option VALUE that represents `storedTz`, matched CANONICALLY so an alias
 * spelling still selects the right option in BOTH directions — a stored Asia/Kolkata selects on a
 * machine whose list has Asia/Calcutta, and the reverse. The list values are already this
 * runtime's canonical zones (from Intl.supportedValuesOf), so canonicalizing the stored value is
 * enough to find its option. Returns "" for empty/invalid (picker stays blank -> chain floors to
 * Pacific). Use it for the picker's selected value so an alias mismatch never shows as blank.
 */
export function pickerSelectedValue(storedTz: string | null | undefined): string {
  if (!storedTz) return "";
  const canonical = canonicalZone(storedTz);
  if (DISPLAY_TIMEZONES.some((o) => o.value === canonical)) return canonical;
  // Curated-fallback runtimes (no supportedValuesOf): accept the raw value if it's listed.
  return DISPLAY_TIMEZONES.some((o) => o.value === storedTz) ? storedTz : "";
}

/** Friendly label for an IANA zone (falls back to the raw zone string). */
export function displayTimezoneLabel(zone: string | null | undefined): string {
  if (!zone) return "Account default";
  return FRIENDLY_LABELS[zone] || DISPLAY_TIMEZONES.find((z) => z.value === zone)?.text || zone;
}
